import { AssetCategory } from '../../../Utils/accountUtils';
import { NPS_ICON_MAPPING, NPS_CATEGORY } from '../../../Utils/constants';
import {
  analyticsStatusDataRefType,
  Icon,
  NPSDataObject,
  ParsedAccountAnalysis,
  ParsedNPSResponse,
  ParsedNPSSchemeHolding,
  ParsedSchemeAnalysis,
  RawNPSDataObject,
} from '../../../Utils/types';
import {
  calculateMonths,
  checkIsNumeric,
  getAssetClass,
  getSchemeCategory,
} from '../../../Utils/utils';
import { doesFiTypeExist } from './assetUtils';

type BreakdownProps = {
  holdings: ParsedNPSSchemeHolding[];
};

export type DistributionItem = {
  type: string;
  pct: number;
};

export type BreakdownItem = {
  value: number;
  logo: React.FC<React.SVGProps<SVGSVGElement>>;
  sublabel?: string;
};

type BreakdownResponse = {
  summaryBreakdown: Record<string, BreakdownItem>;
  distribution: DistributionItem[];
  grandTotal: Number;
};

export const getNpsTotalValue = (
  statusData: any,
  data: any,
  datafromAnalyticsStatus: React.RefObject<analyticsStatusDataRefType | null>
): number => {
  if (!doesFiTypeExist(AssetCategory.NPS, statusData)) {
    return 0;
  }
  const npsAnalysis = (data as any)?.current?.NPS?.data?.overallAnalysis;
  return Math.round(
    npsAnalysis?.totalCurrentValue || datafromAnalyticsStatus.current?.nps || 0
  );
};

export const NpsBreakdown = ({
  holdings = [],
}: BreakdownProps): BreakdownResponse => {
  let grandTotal = 0;
  const summaryBreakdown = holdings.reduce(
    (acc: Record<string, BreakdownItem>, item: ParsedNPSSchemeHolding) => {
      // Extract E / C / G / A from schemeName
      const match = getSchemeCategory(item.schemeName ?? '');

      if (match) {
        const key = NPS_CATEGORY[match];
        const value = item.totalValueOfScheme ?? 0;
        const logoString = key.trim().replace(/\s+/g, '_').toUpperCase();
        if (!acc[key]) {
          acc[key] = {
            value: 0,
            logo: NPS_ICON_MAPPING[logoString as Icon],
          };
        }
        acc[key].value += value;
        grandTotal += value;
      }

      return acc;
    },
    {}
  );
  Object.keys(summaryBreakdown).forEach((key) => {
    if (summaryBreakdown[key].value === 0) {
      delete summaryBreakdown[key];
    }
  });

  const distribution = Object.entries(summaryBreakdown)
    .map(([key, data]) => ({
      type: key,
      pct: Number(((data.value / grandTotal) * 100).toFixed(2)),
    }))
    .filter((item) => item.pct > 0);

  if (grandTotal > 0) {
    summaryBreakdown['Current Value'] = {
      value: grandTotal,
      logo: NPS_ICON_MAPPING['CURRENT_VALUE'],
      sublabel: 'Combined tier value',
    };
  }

  return {
    summaryBreakdown,
    distribution,
    grandTotal,
  };
};

export const parseNormalizedNPSData = (
  normalizedData: NPSDataObject[]
): ParsedNPSResponse => {
  const schemeWiseAnalysis: ParsedSchemeAnalysis[] = [];
  const accountWiseAnalysis: ParsedAccountAnalysis[] = [];
  const tier1HoldingsData: ParsedNPSSchemeHolding[] = [];
  const tier2HoldingsData: ParsedNPSSchemeHolding[] = [];

  let totalPortfolioValue = 0;
  let totalTier1Value = 0;
  let totalTier2Value = 0;
  let activeAccounts = 0;
  let frozenAccounts = 0;

  normalizedData.forEach((item) => {
    const overall = item?.overallAnalysis;
    const tier1 = item?.tier1Analysis;
    const tier2 = item?.tier2Analysis;
    const holder = item?.holderInfo;
    const tier1Holdings = tier1?.holdings ?? [];
    const tier2Holdings = tier2?.holdings ?? [];
    const accountValue = overall?.totalCurrentValue || 0;

    totalPortfolioValue += accountValue;
    totalTier1Value += overall?.tier1CurrentValue || 0;
    totalTier2Value += overall?.tier2CurrentValue || 0;

    if (overall?.status === 'ACTIVE') {
      activeAccounts += 1;
    }
    if (overall?.frozen) {
      frozenAccounts += 1;
    }

    // =========================================
    // HOLDINGS RAW DATA
    // =========================================

    tier1HoldingsData.push(
      ...tier1Holdings.map((holding) => ({
        schemeName: holding?.schemeName ?? null,
        schemeId: holding?.schemeId ?? null,
        schemeCategory: holding?.schemeCategory ?? null,
        assetClass: holding?.assetClass ?? null,
        allocationPercentage: holding?.allocationPercentage ?? null,
        totalUnits: holding?.totalUnits ?? null,
        blockedUnits: holding?.blockedUnits ?? null,
        freeUnits: holding?.freeUnits ?? null,
        nav: holding?.nav ?? null,
        totalValueOfScheme: holding?.totalValueOfScheme ?? null,
      }))
    );
    tier2HoldingsData.push(
      ...tier2Holdings.map((holding) => ({
        schemeName: holding?.schemeName ?? null,
        schemeId: holding?.schemeId ?? null,
        schemeCategory: holding?.schemeCategory ?? null,
        assetClass: holding?.assetClass ?? null,
        allocationPercentage: holding?.allocationPercentage ?? null,
        totalUnits: holding?.totalUnits ?? null,
        blockedUnits: holding?.blockedUnits ?? null,
        freeUnits: holding?.freeUnits ?? null,
        nav: holding?.nav ?? null,
        totalValueOfScheme: holding?.totalValueOfScheme ?? null,
      }))
    );
    // =========================================
    // ACCOUNT ANALYSIS
    // =========================================

    accountWiseAnalysis.push({
      schemeName: holder?.fipName ?? 'NPS',
      schemeType: 'NPS',
      fipName: holder?.fipName ?? '',
      pranId: holder?.maskedPranId ?? '',
      linkedAccRef: item?.linkedAccRef ?? null,
      totalPortfolioValue: accountValue,
      status: overall?.status ?? null,
      tier1Status: overall?.tier1Status ?? null,
      tier2Status: overall?.tier2Status ?? null,
      openingDate: overall?.openingDate ?? null,
      accountAgeInMonths: overall?.accountAgeInMonths ?? null,
      equityAssetValue: overall?.equityAssetValue ?? 0,
      debtAssetValue: overall?.debtAssetValue ?? 0,
      otherAssetValue: overall?.otherAssetValue ?? 0,
      tier1CurrentValue: overall?.tier1CurrentValue ?? null,
      tier2CurrentValue: overall?.tier2CurrentValue ?? null,
      hasTier2: overall?.hasTier2 ?? null,
      frozen: overall?.frozen ?? null,
      inTransition: overall?.inTransition ?? null,
      allocationBreakdown: item?.allocationBreakdown ?? null,
      riskProfile: item?.riskProfile ?? null,
    });

    // =========================================
    // TIER 1 SCHEMES
    // =========================================

    tier1Holdings.forEach((scheme) => {
      schemeWiseAnalysis.push({
        schemeName: scheme?.schemeName ?? null,
        schemeType: 'NPS',
        schemeId: scheme?.schemeId ?? null,
        schemeCategory: scheme?.schemeCategory ?? null,
        assetClass: scheme?.assetClass ?? null,
        fipName: holder?.fipName ?? null,
        pranId: holder?.maskedPranId ?? null,
        totalPortfolioValue: scheme?.totalValueOfScheme ?? null,
        totalInvestedValue: 0,
        allocationPercentage: scheme?.allocationPercentage ?? null,
        totalUnits: scheme?.totalUnits ?? null,
        freeUnits: scheme?.freeUnits ?? null,
        blockedUnits: scheme?.blockedUnits ?? null,
        nav: scheme?.nav ?? null,
        tier: 'Tier1',
      });
    });

    // =========================================
    // TIER 2 SCHEMES
    // =========================================

    tier2Holdings.forEach((scheme) => {
      schemeWiseAnalysis.push({
        schemeName: scheme?.schemeName ?? null,
        schemeType: 'NPS',
        schemeId: scheme?.schemeId ?? null,
        schemeCategory: scheme?.schemeCategory ?? null,
        assetClass: scheme?.assetClass ?? null,
        fipName: holder?.fipName ?? null,
        pranId: holder?.maskedPranId ?? null,
        totalPortfolioValue: scheme?.totalValueOfScheme ?? null,
        totalInvestedValue: 0,
        allocationPercentage: scheme?.allocationPercentage ?? null,
        totalUnits: scheme?.totalUnits ?? null,
        freeUnits: scheme?.freeUnits ?? null,
        blockedUnits: scheme?.blockedUnits ?? null,
        nav: scheme?.nav ?? null,
        tier: 'Tier2',
      });
    });
  });

  return {
    data: {
      overallAnalysis: {
        totalPortfolioValue,
        totalAccounts: normalizedData.length,
        activeAccounts,
        frozenAccounts,
        totalTier1Value,
        totalTier2Value,
      },
      schemeWiseAnalysis,
      accountWiseAnalysis,
      tier1HoldingsData,
      tier2HoldingsData,
    },
  };
};

export const normalizeRawNPSToNPSResponse = (
  rawNPSData: RawNPSDataObject[]
): NPSDataObject[] => {
  const toNumber = checkIsNumeric;

  const enrichHoldings = (schemes: any[] = []) => {
    return schemes.map((scheme) => {
      const category = getSchemeCategory(scheme?.schemeName);

      return {
        ...scheme,
        allocationPercentage: toNumber(scheme?.allocationPercentage),
        totalUnits: toNumber(scheme?.totalUnits),
        blockedUnits: toNumber(scheme?.blockedUnits),
        freeUnits: toNumber(scheme?.freeUnits),
        nav: toNumber(scheme?.nav),
        totalValueOfScheme: toNumber(scheme?.totalValueOfScheme),
        schemeCategory: category,
        assetClass: category ? getAssetClass(category) : 'UNKNOWN',
      };
    });
  };

  return rawNPSData.map((item) => {
    const raw = item?.rawData || {};
    const summary = raw?.Summary || {};
    const holdings = summary?.Holdings || {};
    const holderData = raw?.Profile?.Holders?.Holder;
    const holder = Array.isArray(holderData) ? holderData[0] : holderData || {};
    const tier1Holdings = holdings?.Tier1Holdings?.Tier1Holding || [];
    const tier2Holdings = holdings?.Tier2Holdings?.Tier2Holding || [];
    const normalizedTier1 = enrichHoldings(tier1Holdings);
    const normalizedTier2 = enrichHoldings(tier2Holdings);
    const allHoldings = [...normalizedTier1, ...normalizedTier2];
    const allocationMap = allHoldings.reduce(
      (acc, holding) => {
        const category = holding?.schemeCategory;
        if (!category) {
          return acc;
        }
        if (!acc[category]) {
          acc[category] = 0;
        }
        acc[category] += toNumber(holding?.totalValueOfScheme);
        return acc;
      },
      {} as Record<string, number>
    );
    const currentValue = toNumber(summary?.currentValue);
    const equityValue = allocationMap.E || 0;
    const corporateDebtValue = allocationMap.C || 0;
    const governmentBondValue = allocationMap.G || 0;
    const alternativeValue = allocationMap.A || 0;
    const equityExposure =
      currentValue > 0
        ? Number(((equityValue / currentValue) * 100).toFixed(2))
        : 0;
    const debtExposure =
      currentValue > 0
        ? Number(
            (
              ((corporateDebtValue + governmentBondValue) / currentValue) *
              100
            ).toFixed(2)
          )
        : 0;
    return {
      overallAnalysis: {
        totalCurrentValue: currentValue,
        status: summary?.status,
        openingDate: summary?.openingDate,
        accountAgeInMonths: summary?.openingDate
          ? calculateMonths(summary?.openingDate)
          : null,
        tier1Status: summary?.tier1Status,
        tier2Status: summary?.tier2Status,
        tier1NAVDate: summary?.tier1NAVDate,
        tier2NAVDate: summary?.tier2NAVDate,
        debtAssetValue: corporateDebtValue + governmentBondValue,
        equityAssetValue: equityValue,
        otherAssetValue: alternativeValue,
        tier1CurrentValue: toNumber(holdings?.Tier1Holdings?.currentValue),
        tier2CurrentValue: toNumber(holdings?.Tier2Holdings?.currentValue),
        hasTier2: normalizedTier2.length > 0,
        frozen: summary?.tier1Status === 'FROZEN',
        inTransition:
          toNumber(holdings?.Tier1Holdings?.amountInTransition) > 0 ||
          toNumber(holdings?.Tier2Holdings?.amountInTransition) > 0,
      },
      tier1Analysis: {
        currentValue: toNumber(holdings?.Tier1Holdings?.currentValue),
        amountInTransition: toNumber(
          holdings?.Tier1Holdings?.amountInTransition
        ),
        fundHouseName: raw?.fipName,
        fundHouseCode: normalizedTier1?.[0]?.schemeId?.slice(0, 5),
        schemePreferenceType: holdings?.Tier1Holdings?.schemePreferenceType,
        holdings: normalizedTier1,
      },
      tier2Analysis: {
        currentValue: toNumber(holdings?.Tier2Holdings?.currentValue),
        amountInTransition: toNumber(
          holdings?.Tier2Holdings?.amountInTransition
        ),
        fundHouseName: normalizedTier2.length ? raw?.fipName : null,
        fundHouseCode: normalizedTier2?.[0]?.schemeId?.slice(0, 5) || null,
        schemePreferenceType:
          holdings?.Tier2Holdings?.schemePreferenceType || null,
        holdings: normalizedTier2,
      },
      allocationBreakdown: {
        equity: {
          schemeCategory: 'E',
          percentage: equityExposure,
          value: equityValue,
        },
        corporateBonds: {
          schemeCategory: 'C',
          percentage:
            currentValue > 0
              ? Number(((corporateDebtValue / currentValue) * 100).toFixed(2))
              : 0,
          value: corporateDebtValue,
        },
        government: {
          schemeCategory: 'G',
          percentage:
            currentValue > 0
              ? Number(((governmentBondValue / currentValue) * 100).toFixed(2))
              : 0,
          value: governmentBondValue,
        },
        alternative:
          alternativeValue > 0
            ? {
                schemeCategory: 'A',
                percentage:
                  currentValue > 0
                    ? Number(
                        ((alternativeValue / currentValue) * 100).toFixed(2)
                      )
                    : 0,
                value: alternativeValue,
              }
            : null,
      },
      riskProfile: {
        equityExposure,
        debtExposure,
        riskCategory:
          equityExposure > 60
            ? 'AGGRESSIVE'
            : equityExposure > 40
              ? 'MODERATE'
              : 'CONSERVATIVE',
        investmentChoice: holdings?.Tier1Holdings?.schemePreferenceType,
      },
      holderInfo: {
        name: holder?.name,
        maskedPranId: holder?.pranId || '',
        nominee: holder?.nominee,
        fipId: null,
        fipName: raw?.fipName,
      },
      linkedAccRef: raw?.linkedAccRef,
    };
  });
};
